import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import type { TabHeader } from '@shared/components/tabs/tabs.component';
import { BehaviorSubject } from 'rxjs';

/**
 * Main action of the current page (e.g. "Nuevo Pedido"), rendered in the topbar.
 * The table publishes it from its action button; pages without a table can set it too.
 */
export interface PageAction {
	label: string;
	icon?: string;
	onClick: (origin: EventTarget | null) => void;
}

/**
 * Tabs of the current page (e.g. "Todos / Mis Pedidos"), rendered in the topbar on desktop.
 * `selected` is kept in sync by whoever changes the tab.
 */
export interface PageTabs {
	tabs: TabHeader[];
	selected: number;
	onChange: (index: number) => void;
}

@Injectable({
	providedIn: 'root',
})
export class MenuService {
	constructor(@Inject(PLATFORM_ID) private platformId: object) {}

	private pageActionSubject = new BehaviorSubject<PageAction | null>(null);
	pageAction$ = this.pageActionSubject.asObservable();

	setPageAction(action: PageAction): void {
		this.pageActionSubject.next(action);
	}

	/** Clears the action only if it is still `action` (another page may have replaced it) */
	clearPageAction(action: PageAction): void {
		if (this.pageActionSubject.value === action) {
			this.pageActionSubject.next(null);
		}
	}

	private pageTabsSubject = new BehaviorSubject<PageTabs | null>(null);
	pageTabs$ = this.pageTabsSubject.asObservable();

	setPageTabs(tabs: PageTabs): void {
		this.pageTabsSubject.next(tabs);
	}

	clearPageTabs(tabs: PageTabs): void {
		if (this.pageTabsSubject.value === tabs) {
			this.pageTabsSubject.next(null);
		}
	}

	private readonly STORAGE_KEY = 'menuState'; // Key used to store menu states in localStorage

	// BehaviorSubject to track the menu state
	private menuStateSubject = new BehaviorSubject<{ [key: string]: boolean }>(this.loadState());

	menuState$ = this.menuStateSubject.asObservable();

	// BehaviorSubject for profile modal state
	private profileModalStateSubject = new BehaviorSubject<boolean>(false);
	profileModalState$ = this.profileModalStateSubject.asObservable();

	/**
	 * Opens or closes the profile modal
	 */
	setProfileModalState(visible: boolean): void {
		this.profileModalStateSubject.next(visible);
	}

	/**
	 * Toggles the state of a specific menu and updates localStorage.
	 * @param menuKey - The key identifying the menu (e.g., 'sidebar', 'profileMenu').
	 */
	toggleMenu(menuKey: string): void {
		const currentState = this.menuStateSubject.value;
		const newState = {
			...currentState,
			[menuKey]: !currentState[menuKey],
		};
		this.menuStateSubject.next(newState);
		this.saveState(newState);
	}

	/**
	 * Opens a specific menu and updates localStorage.
	 * @param menuKey - The key identifying the menu to open.
	 */
	openMenu(menuKey: string): void {
		const currentState = this.menuStateSubject.value;
		const newState = {
			...currentState,
			[menuKey]: true,
		};
		this.menuStateSubject.next(newState);
		this.saveState(newState);
	}

	/**
	 * Closes a specific menu and updates localStorage.
	 * @param menuKey - The key identifying the menu to close.
	 */
	closeMenu(menuKey: string): void {
		const currentState = this.menuStateSubject.value;
		const newState = {
			...currentState,
			[menuKey]: false,
		};
		this.menuStateSubject.next(newState);
		this.saveState(newState);
	}

	/**
	 * Loads the menu state from localStorage.
	 * If no state is found, defaults are returned.
	 * @returns An object representing the menu state.
	 */
	private loadState(): { [key: string]: boolean } {
		if (!isPlatformBrowser(this.platformId)) {
			return { sidebar: true, profileMenu: false, backoffice: true }; // Default values for SSR
		}
		const savedState = localStorage.getItem(this.STORAGE_KEY);
		return savedState ? JSON.parse(savedState) : { sidebar: true, profileMenu: false, backoffice: true }; // Default values
	}

	/**
	 * Saves the current menu state to localStorage.
	 * @param state - The state object to be saved.
	 */
	private saveState(state: { [key: string]: boolean }): void {
		if (isPlatformBrowser(this.platformId)) {
			localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
		}
	}

	/**
	 * Retrieves the current state of a specific menu.
	 * @param menuKey - The key identifying the menu to retrieve.
	 * @returns A boolean representing the menu's state.
	 */
	getMenuState(menuKey: string): boolean {
		return this.menuStateSubject.value[menuKey];
	}
}
